import { ChangeEvent, Fragment, useState } from 'react'
import { NavBar } from 'components/NavBar/NavBar'
import { NoData } from 'components/NoData'
import { Button } from 'components/ui/Button/Button'
import { Input } from 'components/Input/Input'
import { PageWrapper, Tabs, TabsContainer } from 'styling/GlobalStyles'
import { BodyText, Head1, OtherText } from 'styling/Typography'
import { DemoBadge } from 'pages/Operations/Shared/OpsSharedComponents'
import { USE_OPS_MOCKS } from 'pages/Operations/Shared/opsControl'
import { OPS_EVENTS } from 'pages/Operations/Shared/opsControlMocks'
import { IOpsEvent, OpsAlertSeverity, OpsDepartmentKey, OpsTaskSource } from 'types/operationsControl.interface'
import { ITimelineEventGroup, ITimelineTimeFilterOption } from 'types/opsTimeline.interface'
import { DEPARTMENT_FILTERS, matchesTimeFilter, SEVERITY_FILTERS, SOURCE_FILTERS, TIME_FILTERS } from './timelineMeta'
import { TimelineEventRow } from './TimelineEventRow'
import { TimelineGroupRow } from './TimelineGroupRow'
import {
  EmptyStreamWrap,
  FilterBar,
  FilterGroup,
  FilterLabel,
  HeaderRow,
  MarkerDot,
  MarkerLabel,
  MarkerRow,
  SearchWrap,
  StreamLine,
  StreamWrap,
  TapHint,
} from './OpsTimeline.styled'

const GROUP_MIN_SIZE = 3

const buildGroups = (list: IOpsEvent[], grouping: boolean): ITimelineEventGroup[] => {
  if (!grouping) return list.map((e) => ({ key: `single-${e.id}`, time: e.time, events: [e] }))
  const groups: ITimelineEventGroup[] = []
  list.forEach((e) => {
    const current = groups[groups.length - 1]
    const head = current ? current.events[0] : null
    if (head && head.time === e.time && head.department === e.department && head.kind === e.kind) {
      current.events.push(e)
    } else {
      groups.push({ key: `${e.time}-${e.department || 'site'}-${e.kind}-${e.id}`, time: e.time, events: [e] })
    }
  })
  return groups
}

export const OpsTimeline = () => {
  const [departmentFilter, setDepartmentFilter] = useState<'ALL' | OpsDepartmentKey>('ALL')
  const [severityFilter, setSeverityFilter] = useState<'ALL' | OpsAlertSeverity>('ALL')
  const [sourceFilter, setSourceFilter] = useState<'ALL' | OpsTaskSource>('ALL')
  const [timeFilter, setTimeFilter] = useState<ITimelineTimeFilterOption['key']>('ALL')
  const [search, setSearch] = useState('')
  const [expandedGroups, setExpandedGroups] = useState<{ [k: string]: boolean }>({})

  const query = search.trim().toLowerCase()

  const events = OPS_EVENTS.filter(
    (e) =>
      (departmentFilter === 'ALL' ||
        e.department === null ||
        e.department === departmentFilter ||
        e.deptTo === departmentFilter) &&
      (severityFilter === 'ALL' ||
        e.severity === severityFilter ||
        (severityFilter === 'info' && e.severity === 'success')) &&
      (sourceFilter === 'ALL' || e.source === null || e.source === sourceFilter) &&
      matchesTimeFilter(timeFilter, e.time) &&
      (!query ||
        e.title.toLowerCase().includes(query) ||
        e.description.toLowerCase().includes(query)),
  ).reverse()

  const groups = buildGroups(events, !query)

  const toggleGroup = (key: string) =>
    setExpandedGroups((prev) => ({ ...prev, [key]: !prev[key] }))

  return (
    <>
      <NavBar />
      <PageWrapper>
        <HeaderRow>
          <Head1>Shift timeline</Head1>
          <DemoBadge />
          <Button variant='black' size='sm' onClick={() => window.print()}>
            Print / PDF
          </Button>
          {USE_OPS_MOCKS && (
            <SearchWrap>
              <Input
                nomarg
                search
                noinvalidtext
                placeholder='Search events…'
                value={search}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
              />
            </SearchWrap>
          )}
        </HeaderRow>
        <OtherText grey>Day Shift · started 06:00 · live</OtherText>
        {USE_OPS_MOCKS ? (
          <>
            <FilterBar>
              <FilterGroup>
                <FilterLabel>Department</FilterLabel>
                <TabsContainer>
                  {DEPARTMENT_FILTERS.map((f) => (
                    <Tabs key={f.key} active={departmentFilter === f.key} onClick={() => setDepartmentFilter(f.key)}>
                      {f.label}
                    </Tabs>
                  ))}
                </TabsContainer>
              </FilterGroup>
              <FilterGroup>
                <FilterLabel>Severity</FilterLabel>
                <TabsContainer>
                  {SEVERITY_FILTERS.map((f) => (
                    <Tabs key={f.key} active={severityFilter === f.key} onClick={() => setSeverityFilter(f.key)}>
                      {f.label}
                    </Tabs>
                  ))}
                </TabsContainer>
              </FilterGroup>
              <FilterGroup>
                <FilterLabel>Source</FilterLabel>
                <TabsContainer>
                  {SOURCE_FILTERS.map((f) => (
                    <Tabs key={f.key} active={sourceFilter === f.key} onClick={() => setSourceFilter(f.key)}>
                      {f.label}
                    </Tabs>
                  ))}
                </TabsContainer>
              </FilterGroup>
              <FilterGroup>
                <FilterLabel>Time</FilterLabel>
                <TabsContainer>
                  {TIME_FILTERS.map((f) => (
                    <Tabs key={f.key} active={timeFilter === f.key} onClick={() => setTimeFilter(f.key)}>
                      {f.label}
                    </Tabs>
                  ))}
                </TabsContainer>
              </FilterGroup>
            </FilterBar>
            <TapHint grey>Tap an event to open the task, plan or machine behind it.</TapHint>
            {events.length ? (
              <StreamWrap>
                <StreamLine />
                <MarkerRow>
                  <span />
                  <MarkerDot />
                  <MarkerLabel>Now · 07:52</MarkerLabel>
                </MarkerRow>
                {groups.map((g) =>
                  g.events.length >= GROUP_MIN_SIZE ? (
                    <TimelineGroupRow
                      key={g.key}
                      group={g}
                      expanded={!!expandedGroups[g.key]}
                      onToggle={() => toggleGroup(g.key)}
                    />
                  ) : (
                    <Fragment key={g.key}>
                      {g.events.map((e) => (
                        <TimelineEventRow key={e.id} event={e} />
                      ))}
                    </Fragment>
                  ),
                )}
                <MarkerRow>
                  <span />
                  <MarkerDot $muted />
                  <MarkerLabel $muted>Shift start · 06:00</MarkerLabel>
                </MarkerRow>
              </StreamWrap>
            ) : (
              <EmptyStreamWrap>
                <BodyText grey>No events match these filters.</BodyText>
              </EmptyStreamWrap>
            )}
          </>
        ) : (
          <NoData text='The shift timeline will populate once the events feed is connected.' />
        )}
      </PageWrapper>
    </>
  )
}
